/**
 * Realtime → TanStack Query cache bindings for the PUBLIC app (SSOT).
 *
 * Mirrors the staff app's `features/realtime/query-bindings.ts`: the generic
 * `@letsgethome/realtime` package cannot import app-private `queryKeys` (DAG:
 * realtime sits below the apps), so it exposes the `onEnvelope` injection seam
 * on `RealtimeProvider` — the client's `addEnvelopeListener` central seam,
 * which sees EVERY envelope before per-channel component fan-out. This module
 * is the injected policy, keyed exhaustively by the proto oneof
 * `payload.case`.
 *
 * PR-E2: the backend now has a production realtime producer for
 * conversations. Both conversation payloads invalidate the two inbox surfaces
 * — the Messages tab-badge unread-count query and (W-6) the sidebar
 * conversation list — so a new message bumps the recipient's badge AND
 * re-orders/re-previews the row beside it, and a read marker (including this
 * user's own, echoed back on the thread channel) clears both. The realtime
 * event is only a *trigger*; the RPC behind each query stays the source of
 * truth, so we invalidate rather than write cache from the wire.
 *
 * The `satisfies Record<EnvelopePayloadCase, …>` makes the map EXHAUSTIVE:
 * adding a new realtime payload to `realtime.v1.Envelope` is a compile error
 * here until its cache effect is declared.
 */
import { queryKeys } from '@/shared/lib/query-keys';
import type { Envelope } from '@letsgethome/realtime/events';
import type { QueryClient } from '@tanstack/react-query';

/** Every non-empty payload case of the realtime `Envelope` oneof. */
export type EnvelopePayloadCase = Exclude<Envelope['payload']['case'], undefined>;

/** What a realtime envelope does to the query cache. */
type EnvelopeBinding = (envelope: Envelope, queryClient: QueryClient) => void;

/** Explicit "this event triggers no query invalidation" (a reviewed choice). */
const noInvalidation: EnvelopeBinding = () => {};

/**
 * Conversation events drive BOTH surfaces that show unread: the Messages tab
 * badge (`useUnreadConversationsCount`) and the inbox sidebar list
 * (`useConversationList`, W-6). Envelopes only arrive for channels some
 * component subscribed to — `conversations:{id}` from an open thread, and
 * (WP-E) `notifications:{userId}` for the signed-in user, which
 * `useNotificationEvents` now subscribes. This cannot flood: message cadence
 * is human-typing cadence.
 *
 * W-6: the sidebar used to be a loader snapshot with no binding at all, so the
 * badge moved and the rows beside it did not. Refreshing them from ONE place
 * is what keeps them consistent — a new message re-orders the list, updates the
 * preview and moves the unread dot exactly when the badge bumps; a read marker
 * clears both.
 *
 * Both keys are named EXPLICITLY rather than invalidating the shared
 * `conversations.all` prefix, even though both nest under it: the prefix also
 * matches `conversations.thread`, which is a write-only store with
 * `queryFn: skipToken`, and TanStack refetches every ACTIVE match — for a
 * skipToken query that means a dev-time `console.error` plus a rejected fetch
 * on every incoming message. See the note on `queryKeys.conversations.all`.
 *
 * INVALIDATES rather than writes. The per-user envelope carries an
 * `unread_count`, but that count is PER-CONVERSATION while the badge counts
 * CONVERSATIONS WITH UNREAD — two different numbers, and assigning one to the
 * other would render a confidently wrong badge.
 */
const invalidateInboxSurfaces: EnvelopeBinding = (_envelope, queryClient) => {
  void queryClient.invalidateQueries({ queryKey: queryKeys.conversations.unreadCount });
  void queryClient.invalidateQueries({ queryKey: queryKeys.conversations.list });
};

export const REALTIME_QUERY_BINDINGS = {
  conversationMessageReceived: invalidateInboxSurfaces,
  conversationReadMarker: invalidateInboxSurfaces,

  // Consumed by their feature components / staff-only surfaces; no public
  // query-cache effect. Declared explicitly so the map stays exhaustive.
  presenceUpdate: noInvalidation,
  heartbeat: noInvalidation,
  postProcessingProgress: noInvalidation,
} satisfies Record<EnvelopePayloadCase, EnvelopeBinding>;

/**
 * Apply the registered binding for one envelope. Wired into
 * `<RealtimeProvider onEnvelope=…>` via `PublicRealtimeProvider`.
 */
export function applyRealtimeQueryBindings(envelope: Envelope, queryClient: QueryClient): void {
  const payloadCase = envelope.payload.case;
  if (payloadCase === undefined) return; // empty oneof — nothing to bridge
  // Exhaustive map ⇒ the binding is always defined for a real case.
  REALTIME_QUERY_BINDINGS[payloadCase](envelope, queryClient);
}
